import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; data?: unknown }
  if (!b?.success) {
    throw new Error(sanitizeBusinessMessage(b?.message, 'Request failed'))
  }
  return b.data
}

function apiError(err: unknown, fallback: string) {
  return toUserFacingApiError(err, fallback)
}

export type WalletSummary = {
  storeId: number
  currency: string
  availableBalance: string
  pendingSettlementBalance?: string
  totalCredited: string
  totalWithdrawn: string
  pendingWithdrawalAmount: string
  nextSettlementAt?: string | null
}

export type GatewayToggleRow = {
  platformPaymentGatewayId: number
  slug?: string
  provider: string
  displayName: string
  logoUrl?: string | null
  supportedCurrencies: unknown
  isEnabled: boolean
  sortOrder: number
  publicKey?: string
}

/**
 * @param currency Optional ISO 4217 code. When omitted, the API uses the **organisation** default —
 *   which may not match the store’s trading / order currency. Prefer passing the selected store’s effective currency.
 */
export async function fetchStoreWallet(storeId: number, currency?: string): Promise<WalletSummary> {
  try {
    const params =
      currency && String(currency).trim() !== ''
        ? { currency: String(currency).trim().toUpperCase() }
        : undefined
    const { data } = await api.get(`/storebusiness/stores/${storeId}/wallet`, { params })
    return assertSuccess(data) as WalletSummary
  } catch (e) {
    throw apiError(e, 'Failed to load wallet')
  }
}

export async function fetchStorePaymentGateways(storeId: number): Promise<{ items: GatewayToggleRow[] }> {
  try {
    const { data } = await api.get(`/storebusiness/stores/${storeId}/payment-gateways`)
    return assertSuccess(data) as { items: GatewayToggleRow[] }
  } catch (e) {
    throw apiError(e, 'Failed to load payment gateways')
  }
}

export async function saveStorePaymentGateways(
  storeId: number,
  items: Array<{ platformPaymentGatewayId: number; isEnabled: boolean; sortOrder?: number }>,
) {
  try {
    const { data } = await api.put(`/storebusiness/stores/${storeId}/payment-gateways`, { items })
    return assertSuccess(data) as { items: GatewayToggleRow[] }
  } catch (e) {
    throw apiError(e, 'Failed to save payment gateways')
  }
}

export type WithdrawalRequestRow = {
  id: number
  storeId: number
  amount: string
  currency: string
  status: string
  createdAt: string
  processedAt: string | null
  rejectionReason?: string | null
}

export async function fetchWithdrawalRequests(params: { page?: number; limit?: number; storeId?: number }) {
  try {
    const { data } = await api.get('/storebusiness/withdrawal-requests', { params })
    return assertSuccess(data) as {
      items: WithdrawalRequestRow[]
      total: number
      page: number
      limit: number
    }
  } catch (e) {
    throw apiError(e, 'Failed to load withdrawals')
  }
}

export async function createWithdrawalRequest(
  storeId: number,
  body: { amount: number; currency?: string; payoutSnapshot?: Record<string, unknown> },
) {
  try {
    const { data } = await api.post(`/storebusiness/stores/${storeId}/withdrawal-requests`, body)
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to create withdrawal')
  }
}

export type PickupLocationRow = {
  id: number
  storeId: number
  name: string
  addressText: string
  feeAmount: string
  sortOrder: number
  createdAt?: string
  updatedAt?: string
}

export async function fetchPickupLocations(storeId: number): Promise<{ items: PickupLocationRow[] }> {
  try {
    const { data } = await api.get(`/storebusiness/stores/${storeId}/pickup-locations`)
    return assertSuccess(data) as { items: PickupLocationRow[] }
  } catch (e) {
    throw apiError(e, 'Failed to load pickup locations')
  }
}

export async function createPickupLocation(
  storeId: number,
  body: { name: string; addressText: string; feeAmount?: number | null },
) {
  try {
    const { data } = await api.post(`/storebusiness/stores/${storeId}/pickup-locations`, body)
    return assertSuccess(data) as PickupLocationRow
  } catch (e) {
    throw apiError(e, 'Failed to create pickup location')
  }
}

export async function updatePickupLocation(
  storeId: number,
  id: number,
  body: Partial<{ name: string; addressText: string; feeAmount: number | null; sortOrder: number }>,
) {
  try {
    const { data } = await api.patch(`/storebusiness/stores/${storeId}/pickup-locations/${id}`, body)
    return assertSuccess(data) as PickupLocationRow
  } catch (e) {
    throw apiError(e, 'Failed to update pickup location')
  }
}

export async function deletePickupLocation(storeId: number, id: number) {
  try {
    const { data } = await api.delete(`/storebusiness/stores/${storeId}/pickup-locations/${id}`)
    return assertSuccess(data) as { deleted: boolean }
  } catch (e) {
    throw apiError(e, 'Failed to delete pickup location')
  }
}
